import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { supabase } from "@/api/supabase";

/**
 * Detects a Supabase password-recovery link and redirects to
 * /update-password, mirroring the two checks in the Flutter app's
 * `GoRouter.redirect` (`app_router.dart`):
 *
 *   1. The recovery link lands with `type=recovery` in the URL *fragment*
 *      (Supabase's implicit-flow redirect shape), checked once on mount /
 *      whenever the location changes.
 *   2. Supabase additionally fires a `PASSWORD_RECOVERY` auth event, which
 *      can arrive after the fragment has already been consumed/cleared by
 *      the client — so both signals are watched independently.
 *
 * /update-password itself is always reachable regardless of session state
 * (see router.tsx) — this watcher just gets the user there from wherever
 * they landed.
 */
function isRecoveryFragment(hash: string): boolean {
  if (!hash) {
    return false;
  }
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  return params.get("type") === "recovery";
}

export function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/update-password" && isRecoveryFragment(location.hash)) {
      void navigate("/update-password", { replace: true });
    }
  }, [location.pathname, location.hash, navigate]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        void navigate("/update-password", { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  return <Outlet />;
}
